import { db } from '../config/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter
} from 'firebase/firestore';

/**
 * Marketplace Service
 * Browse and discover courses and sessions from all instructors
 */

class MarketplaceService {
  /**
   * Get all public courses
   */
  async getAllCourses(filters = {}, limitCount = 20, lastDoc = null) {
    try {
      const coursesRef = collection(db, 'courses');
      let constraints = [
        where('status', '==', 'approved'),
        where('isPublic', '==', true)
      ];
      
      // Add filters
      if (filters.category) {
        constraints.push(where('category', '==', filters.category));
      }
      
      if (filters.difficulty) {
        constraints.push(where('difficulty', '==', filters.difficulty));
      }
      
      if (filters.price === 'free') {
        constraints.push(where('price', '==', 0));
      } else if (filters.price === 'paid') {
        constraints.push(where('price', '>', 0));
      }
      
      // Sorting
      const sortBy = filters.sortBy || 'createdAt';
      const sortOrder = filters.sortOrder || 'desc';
      constraints.push(orderBy(sortBy, sortOrder));
      
      constraints.push(limit(limitCount));
      
      if (lastDoc) {
        constraints.push(startAfter(lastDoc));
      }
      
      const q = query(coursesRef, ...constraints);
      const snapshot = await getDocs(q);
      
      const courses = await Promise.all(
        snapshot.docs.map(async (doc) => {
          const courseData = { id: doc.id, ...doc.data() };
          
          // Enrich with instructor info
          if (courseData.instructorId) {
            const instructor = await this.getInstructorInfo(courseData.instructorId);
            courseData.instructor = instructor;
          }
          
          return courseData;
        })
      );
      
      return {
        courses,
        lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
        hasMore: snapshot.docs.length === limitCount
      };
    } catch (error) {
      console.error('Error getting courses:', error);
      return { courses: [], lastDoc: null, hasMore: false };
    }
  }

  /**
   * Get all public sessions
   */
  async getAllSessions(filters = {}, limitCount = 20) {
    try {
      const sessionsRef = collection(db, 'sessions');
      let constraints = [
        where('status', '==', 'approved'),
        where('isPublic', '==', true)
      ];
      
      // Add filters
      if (filters.category) {
        constraints.push(where('category', '==', filters.category));
      }
      
      if (filters.upcoming) {
        const now = new Date();
        constraints.push(where('startDate', '>=', now));
      }
      
      constraints.push(orderBy('startDate', 'asc'));
      constraints.push(limit(limitCount));
      
      const q = query(sessionsRef, ...constraints);
      const snapshot = await getDocs(q);
      
      const sessions = await Promise.all(
        snapshot.docs.map(async (doc) => {
          const sessionData = { id: doc.id, ...doc.data() };
          
          if (sessionData.instructorId) {
            const instructor = await this.getInstructorInfo(sessionData.instructorId);
            sessionData.instructor = instructor;
          }
          
          return sessionData;
        })
      );
      
      return sessions;
    } catch (error) {
      console.error('Error getting sessions:', error);
      return [];
    }
  }

  /**
   * Get course by ID (with full details)
   */
  async getCourseDetails(courseId) {
    try {
      const courseRef = doc(db, 'courses', courseId);
      const courseDoc = await getDoc(courseRef);
      
      if (!courseDoc.exists()) return null;
      
      const courseData = { id: courseDoc.id, ...courseDoc.data() };
      
      // Get instructor details
      if (courseData.instructorId) {
        const instructor = await this.getInstructorInfo(courseData.instructorId);
        courseData.instructor = instructor;
        
        // Get instructor's other courses
        const otherCourses = await this.getInstructorCourses(courseData.instructorId, courseId);
        courseData.instructorOtherCourses = otherCourses;
      }
      
      // Get reviews
      const reviews = await this.getCourseReviews(courseId);
      courseData.reviews = reviews;
      
      return courseData;
    } catch (error) {
      console.error('Error getting course details:', error);
      return null;
    }
  }

  /**
   * Search courses
   */
  async searchCourses(searchTerm, limitCount = 20) {
    try {
      // Note: Firestore doesn't support full-text search natively
      // For production, use Algolia or Elasticsearch
      // This is a basic implementation
      
      const coursesRef = collection(db, 'courses');
      const q = query(
        coursesRef,
        where('status', '==', 'approved'),
        where('isPublic', '==', true),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(q);
      
      // Filter by search term (client-side)
      const searchLower = searchTerm.toLowerCase();
      const filtered = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(course => 
          course.title?.toLowerCase().includes(searchLower) ||
          course.description?.toLowerCase().includes(searchLower) ||
          course.topics?.some(t => t.name?.toLowerCase().includes(searchLower))
        );
      
      // Enrich with instructor info
      const enriched = await Promise.all(
        filtered.map(async (course) => {
          if (course.instructorId) {
            const instructor = await this.getInstructorInfo(course.instructorId);
            course.instructor = instructor;
          }
          return course;
        })
      );
      
      return enriched;
    } catch (error) {
      console.error('Error searching courses:', error);
      return [];
    }
  }

  /**
   * Get featured/trending courses
   */
  async getFeaturedCourses(limitCount = 10) {
    try {
      const coursesRef = collection(db, 'courses');
      const q = query(
        coursesRef,
        where('status', '==', 'approved'),
        where('isPublic', '==', true),
        orderBy('enrollmentCount', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(q);
      
      const courses = await Promise.all(
        snapshot.docs.map(async (doc) => {
          const courseData = { id: doc.id, ...doc.data() };
          if (courseData.instructorId) {
            const instructor = await this.getInstructorInfo(courseData.instructorId);
            courseData.instructor = instructor;
          }
          return courseData;
        })
      );
      
      return courses;
    } catch (error) {
      console.error('Error getting featured courses:', error);
      return [];
    }
  }

  /**
   * Get top-rated courses
   */
  async getTopRatedCourses(limitCount = 10) {
    try {
      const coursesRef = collection(db, 'courses');
      const q = query(
        coursesRef,
        where('status', '==', 'approved'),
        where('isPublic', '==', true),
        where('reviewCount', '>', 0),
        orderBy('reviewCount', 'desc'),
        orderBy('averageRating', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(q);
      
      const courses = await Promise.all(
        snapshot.docs.map(async (doc) => {
          const courseData = { id: doc.id, ...doc.data() };
          if (courseData.instructorId) {
            const instructor = await this.getInstructorInfo(courseData.instructorId);
            courseData.instructor = instructor;
          }
          return courseData;
        })
      );
      
      return courses;
    } catch (error) {
      console.error('Error getting top-rated courses:', error);
      return [];
    }
  }

  /**
   * Get courses by category
   */
  async getCoursesByCategory(category, limitCount = 20) {
    try {
      const coursesRef = collection(db, 'courses');
      const q = query(
        coursesRef,
        where('status', '==', 'approved'),
        where('isPublic', '==', true),
        where('category', '==', category),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(q);
      
      const courses = await Promise.all(
        snapshot.docs.map(async (doc) => {
          const courseData = { id: doc.id, ...doc.data() };
          if (courseData.instructorId) {
            const instructor = await this.getInstructorInfo(courseData.instructorId);
            courseData.instructor = instructor;
          }
          return courseData;
        })
      );
      
      return courses;
    } catch (error) {
      console.error('Error getting courses by category:', error);
      return [];
    }
  }

  /**
   * Get instructor info (cached/lightweight)
   */
  async getInstructorInfo(instructorId) {
    try {
      const instructorRef = doc(db, 'instructors', instructorId);
      const instructorDoc = await getDoc(instructorRef);
      
      if (!instructorDoc.exists()) return null;
      
      const data = instructorDoc.data();
      return {
        id: instructorDoc.id,
        name: data.name,
        profileImage: data.profileImage,
        bio: data.bio,
        expertise: data.expertise,
        totalCourses: data.totalCourses,
        totalStudents: data.totalStudents,
        averageRating: data.averageRating,
        isVerified: data.isVerified
      };
    } catch (error) {
      console.error('Error getting instructor info:', error);
      return null;
    }
  }

  /**
   * Get instructor's other courses
   */
  async getInstructorCourses(instructorId, excludeCourseId = null, limitCount = 5) {
    try {
      const coursesRef = collection(db, 'courses');
      const q = query(
        coursesRef,
        where('instructorId', '==', instructorId),
        where('status', '==', 'approved'),
        where('isPublic', '==', true),
        orderBy('enrollmentCount', 'desc'),
        limit(limitCount + 1)
      );
      
      const snapshot = await getDocs(q);
      let courses = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Exclude current course
      if (excludeCourseId) {
        courses = courses.filter(c => c.id !== excludeCourseId);
      }
      
      return courses.slice(0, limitCount);
    } catch (error) {
      console.error('Error getting instructor courses:', error);
      return [];
    }
  }

  /**
   * Get course reviews
   */
  async getCourseReviews(courseId, limitCount = 10) {
    try {
      const reviewsRef = collection(db, 'courseReviews');
      const q = query(
        reviewsRef,
        where('courseId', '==', courseId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting reviews:', error);
      return [];
    }
  }

  /**
   * Get marketplace statistics
   */
  async getMarketplaceStats() {
    try {
      const coursesRef = collection(db, 'courses');
      const instructorsRef = collection(db, 'instructors');
      
      const [coursesSnapshot, instructorsSnapshot] = await Promise.all([
        getDocs(query(coursesRef, where('status', '==', 'approved'), where('isPublic', '==', true))),
        getDocs(query(instructorsRef, where('isActive', '==', true)))
      ]);
      
      const totalEnrollments = coursesSnapshot.docs.reduce(
        (sum, doc) => sum + (doc.data().enrollmentCount || 0), 
        0
      );
      
      return {
        totalCourses: coursesSnapshot.size,
        totalInstructors: instructorsSnapshot.size,
        totalEnrollments,
        categories: this.getCategoryStats(coursesSnapshot.docs)
      };
    } catch (error) {
      console.error('Error getting marketplace stats:', error);
      return {
        totalCourses: 0,
        totalInstructors: 0,
        totalEnrollments: 0,
        categories: []
      };
    }
  }

  /**
   * Get category statistics
   */
  getCategoryStats(courseDocs) {
    const categories = {};
    
    courseDocs.forEach(doc => {
      const category = doc.data().category || 'Uncategorized';
      if (!categories[category]) {
        categories[category] = 0;
      }
      categories[category]++;
    });
    
    return Object.entries(categories).map(([name, count]) => ({ name, count }));
  }
}

const marketplaceService = new MarketplaceService();
export default marketplaceService;
